/**
 * Chamado de alteração de rateio de contrato (ADR 0094, etapa 7): quanto sai de um centro e vai para
 * outro. A conta é uma só, na abertura (para recusar o que não fecha) e na aprovação (sobre o rateio
 * vigente na competência em que a regra nova começa). Sem ponto flutuante.
 */
import { z } from 'zod';
import { fromScaled, toScaled } from './money.ts';

export const IT_ALLOCATION_METHODS = ['percent', 'amount'] as const;
export type ItAllocationMethod = (typeof IT_ALLOCATION_METHODS)[number];

const VALUE_RE = /^\d{1,15}(\.\d{1,2})?$/;
const scaled = (value: string) => (VALUE_RE.test(value) ? toScaled(value) : 0n);

export const itAllocationChangeInput = z
  .object({
    contractId: z.uuid(),
    fromCostCenterId: z.uuid(),
    toCostCenterId: z.uuid(),
    method: z.enum(IT_ALLOCATION_METHODS),
    value: z
      .string()
      .trim()
      .regex(VALUE_RE, 'Informe um número com até duas casas (ex.: 30 ou 1500.50)'),
  })
  .refine((v) => v.fromCostCenterId !== v.toCostCenterId, {
    path: ['toCostCenterId'],
    message: 'O centro que recebe precisa ser outro',
  })
  .refine((v) => scaled(v.value) > 0n, { path: ['value'], message: 'Precisa ser maior que zero' })
  .refine((v) => v.method !== 'percent' || scaled(v.value) <= 1_000_000n, {
    path: ['value'],
    message: 'Percentual entre 0,01 e 100',
  });
export type ItAllocationChangeInput = z.infer<typeof itAllocationChangeInput>;

/** Rateio vigente do contrato (it.contract_allocation_base), uma linha por centro. */
export interface ContractAllocationBaseLine {
  costCenterId: string | null;
  percent: string | null;
  amount: string | null;
}
export interface ContractAllocationBase {
  mode: string;
  basis: string | null;
  contractAmount: string | null;
  lines: ContractAllocationBaseLine[];
}

export type AllocationShiftError =
  /** Rateio por colaborador: o centro vem de cada pessoa, em Dados Mestres. */
  | 'by_employee'
  /** O centro de origem não está no rateio. */
  | 'from_not_in_split'
  /** Sai mais do que o centro tem. */
  | 'exceeds_share'
  /** Pedido em valor, mas o contrato não tem valor para converter em percentual. */
  | 'no_amount';

export type AllocationShift =
  | {
      ok: true;
      basis: 'percent' | 'amount';
      lines: { costCenterId: string; percent: string | null; amount: string | null }[];
    }
  | { ok: false; code: AllocationShiftError };

/**
 * Tira do centro de origem e põe no de destino, mantendo a soma. Quem ficou com zero sai do rateio.
 * Rateio por valor continua por valor; o demais fica por percentual: um pedido em valor vira
 * percentual sobre o valor do contrato, com duas casas, para valer nas próximas competências.
 */
export function shiftContractAllocation(
  base: ContractAllocationBase,
  change: Pick<ItAllocationChangeInput, 'fromCostCenterId' | 'toCostCenterId' | 'method' | 'value'>,
): AllocationShift {
  if (base.mode === 'employees') return { ok: false, code: 'by_employee' };
  const byAmount = base.basis === 'amount';
  const lines = base.lines.flatMap((l) => {
    const share = byAmount ? l.amount : l.percent;
    return l.costCenterId && share
      ? [{ costCenterId: l.costCenterId, share: toScaled(share) }]
      : [];
  });
  const from = lines.find((l) => l.costCenterId === change.fromCostCenterId);
  if (!from) return { ok: false, code: 'from_not_in_split' };
  const value = toScaled(change.value);
  const HUNDRED = 100n;
  let delta: bigint;
  if (byAmount) {
    const total = lines.reduce((sum, l) => sum + l.share, 0n);
    delta = change.method === 'amount' ? value : (total * value) / (HUNDRED * 10_000n);
  } else if (change.method === 'percent') {
    delta = value;
  } else {
    const total = base.contractAmount ? toScaled(base.contractAmount) : 0n;
    if (total <= 0n) return { ok: false, code: 'no_amount' };
    // Percentual em duas casas: arredonda e guarda na escala de quatro.
    delta = (((value * HUNDRED * 10_000n * 2n + total) / (2n * total) + 50n) / 100n) * 100n;
  }
  if (delta <= 0n || delta > from.share) return { ok: false, code: 'exceeds_share' };
  from.share -= delta;
  const to = lines.find((l) => l.costCenterId === change.toCostCenterId);
  if (to) to.share += delta;
  else lines.push({ costCenterId: change.toCostCenterId, share: delta });
  return {
    ok: true,
    basis: byAmount ? 'amount' : 'percent',
    lines: lines
      .filter((l) => l.share > 0n)
      .map((l) => ({
        costCenterId: l.costCenterId,
        percent: byAmount ? null : fromScaled(l.share),
        amount: byAmount ? fromScaled(l.share) : null,
      })),
  };
}

/** Primeira competência depois de uma data (AAAA-MM-01): a regra nova só vale daí em diante. */
export function nextCompetence(date: string): string {
  const [year, month] = date.split('-').map(Number);
  const next = new Date(Date.UTC(year!, month!, 1));
  return next.toISOString().slice(0, 10);
}
